import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Trash2, CalendarDays, AlertTriangle } from "lucide-react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TYPES = [
  { key: "event", label: "Event", color: "#81B29A" },
  { key: "holiday", label: "No School", color: "#F4A261" },
  { key: "field_trip", label: "Field Trip", color: "#A8DADC" },
  { key: "coop", label: "Co-op", color: "#3D405B" },
  { key: "quarter", label: "Quarter", color: "#457B9D" },
];
const COLOR = { assignment: "#E07A5F", event: "#81B29A", holiday: "#F4A261", field_trip: "#A8DADC", coop: "#3D405B", quarter: "#457B9D" };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [selected, setSelected] = useState(ymd(new Date()));
  const [form, setForm] = useState({ title: "", type: "event", note: "" });
  const [delAsg, setDelAsg] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    api.get("/events").then((r) => {
      const evts = (r.data.events || []).map((e) => ({ ...e, editable: true }));
      const asg = (r.data.assignments || []).map((a) => ({ ...a, editable: false }));
      setItems([...evts, ...asg]);
      setCanEdit(r.data.can_edit);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    items.forEach((it) => { if (it.date) (m[it.date] ||= []).push(it); });
    return m;
  }, [items]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const days = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return cells;
  }, [cursor]);

  const addEvent = async () => {
    if (!form.title.trim()) return toast.error("Give the event a title.");
    try {
      await api.post("/events", { title: form.title, date: selected, type: form.type, note: form.note });
      setForm({ title: "", type: "event", note: "" }); toast.success("Event added!"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`); load(); };

  const delAssignment = async () => {
    if (!delAsg) return;
    setDeleting(true);
    try {
      await api.delete(`/assignments/${delAsg.assignment_id}`);
      toast.success("Assignment deleted.");
      setDelAsg(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const todayStr = ymd(new Date());
  const selItems = byDay[selected] || [];

  return (
    <div>
      <TopBar subtitle="School Calendar" />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Back</button>
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#E07A5F] text-white"><CalendarDays size={20} /></span>
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">School Calendar</h1>
        </div>

        <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6">
          <div className="flex items-center justify-between mb-4">
            <button data-testid="cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 rounded-full hover:bg-[#F2EFE9]"><ChevronLeft size={18} /></button>
            <p className="font-head text-xl font-bold text-[#3D405B]">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</p>
            <button data-testid="cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 rounded-full hover:bg-[#F2EFE9]"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs text-[#6B7280] mb-1">
            {DOW.map((d) => <div key={d} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {grid.map((d, i) => {
              if (!d) return <div key={i} />;
              const key = ymd(d);
              const dayItems = byDay[key] || [];
              const isSel = key === selected;
              const isToday = key === todayStr;
              return (
                <button key={key} data-testid={`cal-day-${d.getDate()}`} onClick={() => setSelected(key)}
                  className={`aspect-square rounded-xl p-1.5 flex flex-col items-center transition-colors ${isSel ? "bg-[#E07A5F] text-white" : "hover:bg-[#F2EFE9]"}`}>
                  <span className={`text-sm ${isToday && !isSel ? "font-bold text-[#E07A5F]" : ""}`}>{d.getDate()}</span>
                  <div className="flex gap-0.5 mt-1 flex-wrap justify-center">
                    {dayItems.slice(0, 3).map((it, j) => <span key={j} className="w-1.5 h-1.5 rounded-full" style={{ background: isSel ? "#fff" : (COLOR[it.type] || "#81B29A") }} />)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-[#EAE6DF] p-6">
          <p className="font-head font-semibold text-[#3D405B] mb-3">{selected}</p>
          <div className="space-y-2 mb-4">
            {selItems.length === 0 && <p className="text-sm text-[#6B7280]">Nothing scheduled.</p>}
            {selItems.map((it, i) => (
              <div key={i} data-testid={`cal-item-${i}`} className="flex items-center justify-between rounded-xl bg-[#F7F5F0] px-4 py-2.5">
                <span className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR[it.type] || "#81B29A" }} />
                  <span><span className="text-[#3D405B] font-medium">{it.title}</span>{it.note && <span className="text-xs text-[#6B7280] ml-2">{it.note}</span>}</span>
                </span>
                {it.editable && canEdit && <button data-testid={`cal-del-${it.id}`} onClick={() => del(it.id)} className="text-[#6B7280] hover:text-[#E07A5F]"><Trash2 size={15} /></button>}
                {it.type === "assignment" && canEdit && <button data-testid={`cal-del-asg-${it.assignment_id}`} onClick={() => setDelAsg(it)} title="Delete assignment" className="text-[#6B7280] hover:text-[#E07A5F]"><Trash2 size={15} /></button>}
              </div>
            ))}
          </div>

          {canEdit && (
            <div className="border-t border-[#EAE6DF] pt-4 flex flex-wrap gap-2 items-center">
              <input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Add an event…"
                className="flex-1 min-w-[160px] rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F]" />
              <select data-testid="event-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="rounded-xl border border-[#EAE6DF] px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F]">
                {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <button data-testid="add-event-btn" onClick={addEvent} className="flex items-center gap-2 rounded-full bg-[#E07A5F] text-white px-5 py-2.5 font-semibold hover:bg-[#c96851] transition-colors"><Plus size={16} /> Add</button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mt-4 text-xs text-[#6B7280]">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLOR.assignment }} /> Assignment due</span>
          {TYPES.map((t) => <span key={t.key} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} /> {t.label}</span>)}
        </div>
      </main>
      {delAsg && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !deleting && setDelAsg(null)}>
          <div data-testid="cal-del-asg-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#E07A5F]/15 text-[#E07A5F] mb-4"><AlertTriangle size={22} /></span>
            <h2 className="font-head text-2xl font-bold text-[#3D405B]">Delete this assignment?</h2>
            <p className="text-[#6B7280] mt-2 leading-relaxed">This permanently removes <b>{delAsg.title}</b>, takes it off the calendar, and deletes any saved grade or progress for it. This can't be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="cal-del-asg-cancel" onClick={() => setDelAsg(null)} disabled={deleting}
                className="flex-1 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors disabled:opacity-60">Cancel</button>
              <button data-testid="cal-del-asg-confirm" onClick={delAssignment} disabled={deleting}
                className="flex-1 rounded-full bg-[#E07A5F] text-white py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">{deleting ? "Deleting…" : "Delete assignment"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
